import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSafeId } from 'site-modules/shared/hooks/use-safe-id';
import Popover from 'reactstrap/lib/Popover'; // eslint-disable-line no-restricted-imports
import { PopperPlacements } from 'reactstrap/lib/utils';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { getCreativeId } from 'client/engagement-handlers/helper';
import { ENTER_KEY_VALUE } from 'site-modules/shared/constants/key-values';

import './popover-item.scss';

export function PopoverItem({
  tooltipContainerTag: TooltipContainerTag = 'span',
  className = null,
  iconClassName = 'small',
  children,
  placement = 'bottom',
  boundariesElement = undefined,
  innerClassName = 'px-0_75 py-0_5 text-cool-gray-30',
  popoverClassName = null,
  delay = undefined,
  fade = undefined,
  screenReaderTooltipLabel,
  renderTarget = null,
  trigger = 'click hover',
  openTrackingParams = null,
  container = undefined,
  offset,
}) {
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const containerRef = useRef();
  const tooltipElementRef = useRef();
  const popoverRef = useRef();
  const popoverId = `popover-${useSafeId()}`;

  const toggle = useCallback(
    event => {
      if (event?.currentTarget && event.currentTarget === popoverRef.current) {
        // exit by Esc keyboard
        tooltipElementRef.current.focus();
      }

      setPopoverOpen(!isPopoverOpen);
    },
    [isPopoverOpen]
  );

  const onKeyDown = useCallback(
    event => {
      if (event.key === ENTER_KEY_VALUE) {
        toggle();
      }
    },
    [toggle]
  );

  useEffect(() => {
    function handleDocumentFocus(event) {
      const element = event.target;
      const subtreeRoot = popoverRef.current;
      const isInDOMSubtree = subtreeRoot && (element === subtreeRoot || subtreeRoot.contains(element));
      if (element !== tooltipElementRef.current && isPopoverOpen && !isInDOMSubtree) {
        setPopoverOpen(!isPopoverOpen);
      }
    }

    if (isPopoverOpen) {
      if (!trigger.includes('focus')) {
        setTimeout(() => {
          popoverRef?.current?.focus();
        });
      }
      document.addEventListener('focus', handleDocumentFocus, true);
    }

    return () => {
      if (isPopoverOpen) {
        document.removeEventListener('focus', handleDocumentFocus, true);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPopoverOpen]);

  useEffect(() => {
    if (isPopoverOpen && openTrackingParams) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_VIEW_CONTENT,
          subaction_name: TrackingConstant.VIEW_TOOLTIP,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
          creative_id: openTrackingParams?.creative_id || getCreativeId(containerRef.current),
          ...openTrackingParams,
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPopoverOpen]);

  const ariaLabel = `Tooltip of ${screenReaderTooltipLabel}`;
  const targetAriaLabel = `View ${ariaLabel}`;
  const reactPopoverTargetProps = {
    role: 'button',
    'aria-label': targetAriaLabel,
    'aria-haspopup': true,
    'aria-expanded': isPopoverOpen,
    'aria-controls': popoverId,
    'data-testid': 'PopoverItem',
    tabIndex: 0,
    onKeyDown,
  };

  return (
    <TooltipContainerTag ref={containerRef} className={classnames('popover-item d-inline-block', className)}>
      {renderTarget ? (
        // eslint-disable-next-line react-hooks/refs -- renderTarget owns the forwarded ref and consumes it outside render.
        renderTarget(reactPopoverTargetProps, tooltipElementRef) // omit onKeyDown for <button>
      ) : (
        <i
          {...reactPopoverTargetProps}
          aria-label={targetAriaLabel}
          ref={tooltipElementRef}
          className={classnames('icon-info info-popover-icon text-center d-inline-block rounded', iconClassName)}
        />
      )}
      <Popover
        id={popoverId}
        className={popoverClassName}
        placement={placement}
        innerClassName={innerClassName}
        target={tooltipElementRef}
        isOpen={isPopoverOpen}
        toggle={toggle}
        container={container || containerRef}
        innerRef={popoverRef}
        tabIndex={-1}
        aria-label={ariaLabel}
        boundariesElement={boundariesElement}
        delay={delay}
        fade={fade}
        trigger={trigger}
        offset={offset}
      >
        {children}
      </Popover>
    </TooltipContainerTag>
  );
}

PopoverItem.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  screenReaderTooltipLabel: PropTypes.string.isRequired,
  tooltipContainerTag: PropTypes.string,
  placement: PropTypes.oneOf(PopperPlacements),
  boundariesElement: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  openTrackingParams: PropTypes.shape({}),
  className: PropTypes.string,
  iconClassName: PropTypes.string,
  innerClassName: PropTypes.string,
  popoverClassName: PropTypes.string,
  delay: PropTypes.shape({ show: PropTypes.number, hide: PropTypes.number }),
  trigger: PropTypes.string,
  fade: PropTypes.bool,
  renderTarget: PropTypes.func,
  container: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ current: PropTypes.any })]),
  offset: PropTypes.arrayOf(PropTypes.number),
};
